/**
 * CSV Template Generation Utilities
 *
 * Functions for generating downloadable CSV templates for various upload flows.
 */

import Papa from 'papaparse';
import { REBATE_CSV_COLUMNS } from '@erp/shared-types';
import { ALL_OTHER_PRODUCTS } from '@erp/shared-utils/src/constants';

/**
 * Generic CSV download utility
 * Handles blob creation and download trigger
 */
export function downloadCSVFile(
  csvContent: string,
  filename = 'template.csv',
): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate example value based on column name (generic fallback)
 * Used when no specific example is provided
 */
export function generateGenericExampleValue(columnName: string): string {
  const lowerCol = columnName.toLowerCase();

  // Common column types
  if (lowerCol === 'modules') return 'rebates';
  if (lowerCol === 'email') return 'example@company.com';
  if (lowerCol === 'name') return 'Example Company Name';
  if (lowerCol === 'aliases') return 'alias1,alias2';
  if (lowerCol === 'geography') return 'North America';
  if (lowerCol === 'description') return 'Example description';
  if (lowerCol === 'internal_code') return 'EXAMPLE';

  // Default fallback
  return `example_${lowerCol.replace(/\s+/g, '_')}`;
}

/**
 * Generate a generic CSV template from column names
 * Uses Papa.unparse for proper RFC 4180 CSV escaping
 */
export function generateGenericCSVTemplate(columns: string[]): string {
  const exampleRow = columns.reduce(
    (acc, col) => {
      acc[col] = generateGenericExampleValue(col);
      return acc;
    },
    {} as Record<string, string>,
  );

  return Papa.unparse([exampleRow], {
    columns,
    header: true,
  });
}

/**
 * Generate and download a CSV template for manual rebate invoice upload
 */
export function downloadRebateInvoiceTemplate(): void {
  const headers = [
    REBATE_CSV_COLUMNS.DATE,
    REBATE_CSV_COLUMNS.DIVISION_NAME,
    REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME,
    REBATE_CSV_COLUMNS.VALUE,
  ];

  // Create example rows with helpful placeholder data
  const exampleRows = [
    {
      [REBATE_CSV_COLUMNS.DATE]: '2025-01-15',
      [REBATE_CSV_COLUMNS.DIVISION_NAME]: 'Nedco Ontario',
      [REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME]: 'Utility Cable',
      [REBATE_CSV_COLUMNS.VALUE]: '-1234.56',
    },
    {
      [REBATE_CSV_COLUMNS.DATE]: '2025-01-15',
      [REBATE_CSV_COLUMNS.DIVISION_NAME]: 'Nedco Quebec',
      [REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME]: 'Industrial Cable',
      [REBATE_CSV_COLUMNS.VALUE]: '-567.89',
    },
    {
      [REBATE_CSV_COLUMNS.DATE]: '2025-01-15',
      [REBATE_CSV_COLUMNS.DIVISION_NAME]: 'Nedco West',
      [REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME]: ALL_OTHER_PRODUCTS,
      [REBATE_CSV_COLUMNS.VALUE]: '-890.12',
    },
  ];

  // Generate CSV content using Papa Parse for proper escaping
  const csvContent = Papa.unparse(exampleRows, {
    columns: headers,
    header: true,
  });

  // Add timestamp to filename
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `rebate-invoice-template-${timestamp}.csv`;

  // Download using shared utility
  downloadCSVFile(csvContent, filename);
}

/**
 * Get template info for display in UI
 */
export function getRebateInvoiceTemplateInfo() {
  return {
    requiredColumns: [
      REBATE_CSV_COLUMNS.DATE,
      REBATE_CSV_COLUMNS.DIVISION_NAME,
      REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME,
      REBATE_CSV_COLUMNS.VALUE,
    ],
    optionalColumns: [],
    examples: {
      [REBATE_CSV_COLUMNS.DATE]: '2025-01-15 (YYYY-MM-DD format)',
      [REBATE_CSV_COLUMNS.DIVISION_NAME]:
        'Division name (must match existing division)',
      [REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME]: `Product group name, or "${ALL_OTHER_PRODUCTS}" for all products`,
      [REBATE_CSV_COLUMNS.VALUE]: 'Transaction amount (negative for rebates)',
    },
  };
}
